export class ApiError extends Error {
  constructor(status, body) {
    super(`HTTP ${status}: ${body}`)
    this.status = status
    this.body = body
  }
}

export class Api {
  constructor({ baseURL = '', getToken = () => null, onUnauthorized = null } = {}) {
    this.baseURL = baseURL
    this.getToken = getToken
    this.onUnauthorized = onUnauthorized
  }

  async _fetch(path, init = {}) {
    const headers = { ...(init.headers || {}) }
    const token = this.getToken()
    if (token) headers.Authorization = `Bearer ${token}`
    const res = await fetch(this.baseURL + path, { ...init, headers })
    if (!res.ok) {
      if (res.status === 401 && this.onUnauthorized) this.onUnauthorized()
      const body = res.text ? await res.text() : ''
      throw new ApiError(res.status, body)
    }
    return res
  }

  async get(path) {
    const res = await this._fetch(path, { method: 'GET' })
    return res.json()
  }

  async post(path, body) {
    const res = await this._fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json()
  }

  async me() {
    return this.get('/api/clarity/me')
  }

  async listClients() {
    return this.get('/api/clarity/clients')
  }

  async createClient(body) {
    return this.post('/api/clarity/clients', body)
  }

  async getClient(id) {
    return this.get(`/api/clarity/clients/${id}`)
  }

  async patchClient(id, partial) {
    const res = await this._fetch(`/api/clarity/clients/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partial),
    })
    return res.json()
  }

  async listClientSessions(id) {
    return this.get(`/api/clarity/clients/${id}/sessions`)
  }

  async preseedFromText(text) {
    return this.post('/api/clarity/clients/preseed', { text })
  }

  async getTenant() {
    return this.get('/api/clarity/tenant')
  }

  async patchTenant(partial) {
    const res = await this._fetch('/api/clarity/tenant', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partial),
    })
    return res.json()
  }

  async archiveExport(sessionId, kind, blob) {
    const fd = new FormData()
    fd.append('file', blob, `${kind}.pdf`)
    const res = await this._fetch(`/api/clarity/sessions/${sessionId}/exports?kind=${encodeURIComponent(kind)}`, {
      method: 'POST',
      body: fd,
    })
    return res.json()
  }

  async listExports(sessionId) {
    return this.get(`/api/clarity/sessions/${sessionId}/exports`)
  }

  // PATCH /api/clarity/sessions/{id} with the full ExtractedFacts blob.
  // Server overwrites the persisted facts JSON in S3; no DDB row change.
  async patchSessionFacts(sessionId, facts) {
    await this._fetch(`/api/clarity/sessions/${sessionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ facts }),
    })
  }

  // DELETE /api/clarity/sessions/{id}. Removes DDB row + best-effort
  // cleanup of the S3 facts blob. Orphan exports are not touched.
  async deleteSession(sessionId) {
    await this._fetch(`/api/clarity/sessions/${sessionId}`, { method: 'DELETE' })
  }

  exportDownloadURL(sessionId, exportId) {
    const t = this.getToken() || ''
    return `${this.baseURL}/api/clarity/sessions/${sessionId}/exports/${exportId}/download?token=${encodeURIComponent(t)}`
  }
}
